import BasicModal from '../common/BasicModal';
import { ChangeEvent, FC, useContext, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Box, Button, Checkbox, FormControl, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { formatNumberWithComma } from '../../utils/number';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import IResponse from '../../interfaces/response.ts';
import { IIncreaseBalance } from '../../interfaces/finance.ts';
import { UserContext } from '../../contexts/UserContext.tsx';

type IPaymentModal = {
	open: boolean;
	onClose: () => void;
	amount: number | null;
	handleResultServer: () => void;
	enableType: boolean;
	description?: string;
	user_id: number;
	handleBalance: (_: any, data: any) => void;
};

type paymentType = 'wallet' | 'online';

const PaymentModalAdmin: FC<IPaymentModal> = ({ open, onClose, amount, description, enableType, user_id, handleBalance }) => {
	const { t } = useTranslation();
	const [isTermsAccepted, setIsTermsAccepted] = useState(false);
	const { userBalance } = useContext(UserContext);
	const { user } = useContext(UserContext);
	const [selectedGateId, setSelectedGateId] = useState<number | null>(null);
	const [type, setType] = useState<paymentType | undefined>();
	const balance = formatNumberWithComma(userBalance);
	const navigate = useNavigate();

	const handleChangeType = (event: ChangeEvent<HTMLInputElement>) => {
		setType((event.target as HTMLInputElement).value as paymentType);
	};

	const finalAmount = formatNumberWithComma(amount!);

	const terms = t('terms');

	const resetState = () => {
		setIsTermsAccepted(false);
		setSelectedGateId(null);
	};

	const handleTermsChange = (event: any) => {
		setIsTermsAccepted(event.target.checked);
	};
	const handleCancel = () => {
		resetState();
		onClose();
	};

	const handlePay = () => {
		axios<IResponse<IIncreaseBalance>>({
			url: '/admin/wallets/charge-user',
			method: 'POST',
			data: {
				user_id: user_id,
				amount: amount,
				description: description,
			},
		}).then(res => {
			if (res.data.success) {
				handleBalance(undefined, user_id);
				onClose();
				navigate('/finance/payments');
			}
		});
	};

	useEffect(() => {
		if (!open) {
			resetState();
		}
	}, [open]);
	const isEnabledSubmit = isTermsAccepted && (selectedGateId || user?.is_admin) && (type || !enableType);

	return (
		<BasicModal className="mx-10 h-auto w-full sm:mx-20" open={open} onClose={onClose} title={t('payment')} showBackIcon={false}>
			<div className="flex w-full flex-col justify-between gap-x-4 px-4 pt-8 sm:px-8">
				<div className="mt-3 w-full rounded-lg bg-grey-100 px-4 pb-6 pt-4 sm:px-6">
					<div className="flex flex-col justify-between sm:flex-row">
						<Typography>{t('payable_price')}</Typography>
						<Trans i18nKey="tooman_amount_label" values={{ amount: finalAmount }} components={[<Typography className="mt-2 sm:mt-0" />]} />
					</div>
				</div>

				{enableType && (
					<Box className="mt-4 w-full rounded-lg bg-grey-100 px-4 pb-6 pt-4 sm:px-6">
						<Typography className="mb-3 font-medium">{t('payment_method')}</Typography>
						<FormControl>
							<RadioGroup value={type} onChange={handleChangeType}>
								<Box className="flex flex-row gap-4">
									<Box className={`rounded border py-1 ps-5 ${type === 'online' ? 'border-primary-main' : 'border-gray-500'}`}>
										<FormControlLabel value="online" control={<Radio />} label={t('online_payment')} />
									</Box>
									<Box className={`rounded border py-1 ps-5 ${type === 'wallet' ? 'border-primary-main' : 'border-gray-500'}`}>
										<FormControlLabel
											value="wallet"
											control={<Radio />}
											label={
												<>
													{t('wallet_payment')}
													<Typography className={'ms-4 inline text-sm text-grey-600'}>
														<Trans i18nKey="current_balance_label" values={{ balance }} />
													</Typography>
												</>
											}
										/>
									</Box>
								</Box>
							</RadioGroup>
						</FormControl>
					</Box>
				)}

				{/*todo:link & change the color "شرایط و ضوابط"*/}
				<div className="mt-4">
					<FormControlLabel label="" control={<Checkbox checked={isTermsAccepted} onChange={handleTermsChange} className="m-0 text-sm font-medium" />} />
					<Link to={`${import.meta.env.VITE_LANDING}/blog/rules`} target={'_blank'} className={'text-primary-main underline'}>
						<Trans i18nKey="accept_terms" values={{ terms }} components={[<Typography className="px-1" />]} />
					</Link>
				</div>

				<div className="mb-8 mt-5 flex w-full flex-col items-center gap-3 sm:flex-row-reverse sm:justify-start">
					<Button variant="outlined" className="w-full border-action-disabledBackground text-action-disabled sm:w-28" onClick={handleCancel}>
						{t('cancel')}
					</Button>
					<Button variant="contained" className="w-full sm:w-60" onClick={handlePay} disabled={!isEnabledSubmit}>
						{t('payment')}
					</Button>
				</div>
			</div>
		</BasicModal>
	);
};

export default PaymentModalAdmin;
